import {
  CheckboxInput,
  FormField,
  Select,
  SelectOption,
  SingleDatePicker,
  TextInput,
} from "@justworkshr/milo-form";
import { useFormikContext } from "formik";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import styles from "./BaseFormFragment.module.css";
import {
  formatToISODate,
  subtractYears,
  toLocalDateValue,
} from "pages/employer-of-record/utils";
import { NATIONALITIES } from "pages/employer-of-record/onboarding/profile-info/constants";

export const initialValues = {
  firstName: "",
  lastName: "",
  preferredFirstName: "",
  middleName: "",
  pronouns: "",
  dateOfBirth: "",
  nationality: "",
  requireVisaSponsorship: "",
  settings: {
    showDateOfBirth: false,
  },
};

const { checkboxField, inputField } = styles;

export type TBaseFormFragmentSchema = Yup.InferType<ReturnType<typeof schema>>;

export const schema = (t: TFunction, workCountry: string) =>
  Yup.object({
    firstName: Yup.string().required(
      t("This field is required. Please enter your legal given name.")
    ),
    lastName: Yup.string().required(
      t("This field is required. Please enter your surname.")
    ),
    middleName: Yup.string().optional(),
    preferredFirstName: Yup.string().optional(),
    pronouns: Yup.string().optional(),
    nationality: Yup.string().required(t("This field is required.")),
    requireVisaSponsorship: Yup.string().when("nationality", {
      is: (nationality: string) => nationality && nationality !== workCountry,
      then: (schema) => schema.required(t("This field is required.")),
      otherwise: (schema) => schema.optional(),
    }),
    dateOfBirth: Yup.string().required(
      "This field is required. Please enter your Date of birth."
    ),
    settings: Yup.object().shape({
      showDateOfBirth: Yup.boolean(),
    }),
  });

export default function BaseFormFragment({
  workCountry,
}: {
  workCountry: string;
}) {
  const { t } = useTranslation();
  const { touched, errors, handleBlur, handleChange, values, setFieldValue } =
    useFormikContext<TBaseFormFragmentSchema>();

  const handleDOBChange = (date: Date) => {
    setFieldValue("dateOfBirth", formatToISODate(date));
  };

  const maxDOB = subtractYears(new Date(), 18);
  const minDOB = new Date("1920-01-01");

  let firstNameMessage = t(
    "Please use the name that appears on your official ID."
  );

  let lastNameMessage = t(
    "Please use the name that appears on your official ID."
  );

  if (workCountry === "GR") {
    firstNameMessage +=
      " Use Greek letters (e.g. Νικη). If a Greek name is not available, use Latin letters (e.g. Nick).";
    lastNameMessage +=
      " Use Greek letters (e.g. Ρίζος). If a Greek name is not available, use Latin letters (e.g. Smith)";
  }

  // Check if nationality is selected and differs from work country to show visa sponsorship field
  const shouldShowVisaSponsorship =
    values.nationality && values.nationality !== workCountry;

  return (
    <>
      <div className={inputField}>
        <FormField
          error={touched?.firstName ? errors?.firstName : ""}
          label={t("Legal given name")}
          message={firstNameMessage}
          required
        >
          <TextInput
            name="firstName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.firstName}
          />
        </FormField>
      </div>

      <div className={inputField}>
        <FormField
          error={touched?.lastName ? errors?.lastName : ""}
          label={t("Surname")}
          message={lastNameMessage}
          required
        >
          <TextInput
            name="lastName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.lastName}
          />
        </FormField>
      </div>

      <div className={inputField}>
        <FormField
          label={t("Preferred name")}
          message={t(
            "This is the name you'll see in your company directory and in emails from us."
          )}
        >
          <TextInput
            name="preferredFirstName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.preferredFirstName}
          />
        </FormField>
      </div>

      <div className={inputField}>
        <FormField label={t("Middle name")}>
          <TextInput
            name="middleName"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.middleName}
          />
        </FormField>
      </div>

      <div className={inputField}>
        <FormField
          label={t("Pronouns")}
          message={t(
            "This will be displayed on your public profile page and in your company org chart."
          )}
        >
          <TextInput
            name="pronouns"
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={t("e.g. He/him, She/her, They/them")}
            value={values.pronouns}
          />
        </FormField>
      </div>

      <div className={inputField}>
        <FormField
          error={touched?.nationality ? errors?.nationality : ""}
          label={t("Nationality")}
          required
        >
          <Select
            name="nationality"
            id="nationality"
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={t("Select Nationality")}
            value={values.nationality}
          >
            {NATIONALITIES.map((nationality) => (
              <SelectOption key={nationality.value} value={nationality.value}>
                {nationality.description}
              </SelectOption>
            ))}
          </Select>
        </FormField>
      </div>

      <div className={inputField}>
        <FormField
          error={touched?.dateOfBirth ? errors?.dateOfBirth : ""}
          label={t("Date of birth")}
          message={t(
            "This will be displayed on your public profile page and in your company org chart."
          )}
          required
        >
          <SingleDatePicker
            id="dateOfBirth"
            name="dateOfBirth"
            onBlur={handleBlur}
            onChange={handleDOBChange}
            value={toLocalDateValue(values.dateOfBirth)}
            minDate={minDOB}
            maxDate={maxDOB}
          />
        </FormField>

        <div className={checkboxField}>
          <CheckboxInput
            checked={values.settings.showDateOfBirth}
            label="Don't show my birthday on my profile"
            name="settings.showDateOfBirth"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.settings.showDateOfBirth ? "true" : "false"}
          />
        </div>

        {shouldShowVisaSponsorship && (
          <div className={checkboxField}>
            <FormField
              required
              label={t(
                "Will you now, or in the future, require sponsorship for employment Visa status"
              )}
              message={t(
                "If you have questions, or if you are not sure, please reach out to support."
              )}
              error={
                touched?.requireVisaSponsorship
                  ? errors?.requireVisaSponsorship
                  : ""
              }
            >
              <Select
                name="requireVisaSponsorship"
                id="requireVisaSponsorship"
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Select an option")}
                value={values.requireVisaSponsorship}
              >
                <SelectOption value="true">{t("Yes")}</SelectOption>
                <SelectOption value="false">{t("No")}</SelectOption>
              </Select>
            </FormField>
          </div>
        )}
      </div>
    </>
  );
}
